<template>
    <Head title="Liste des entrepôts" />

    <AppLayout>
        <div x-data="handleSelect">
            <Datatables>
                <template #title>
                    Liste des entrepôts
                    <span class="font-medium">({{ warehouses.length }})</span>
                </template>
    
                <template #headerButtons>
                    <button v-if="$page.props.acl.isAdmin || $page.props.acl.isCommercial"
                        class="rounded-md py-1 px-2 bg-white text-sky hover:bg-sky-400 hover:text-white"
                        @click="showModal()" title="Ajouter"
                    >
                        <i class="fad fa-plus-circle"></i>
                    </button>
                </template>
    
                <template #tableHeader>
                    <th>#</th>
                    <th>Libellé</th>
                    <th>Description</th>
                    <th>Lien</th>
                    <th>Pays</th>
                    <th>&nbsp;</th>
                </template>
    
                <template #tableBody>
                    <tr v-for="(warehouse, index) in warehouses" :key="warehouse.id">
                        <td>{{ index + 1 }}</td>
                        <td>{{ warehouse.label }}</td>
                        <td>{{ warehouse.description }}</td>
                        <td>
                            <a :href="warehouse.short_link" target="_blank" class="text-sky-700 underline hover:text-sky">
                                {{ warehouse.short_link}}
                            </a>
                        </td>
                        <td>{{ warehouse.country.name }}</td>
                        <td>
                            <div class="flex justify-end items-center space-x-3">
                                <i @click="showModal(warehouse)" title="Modifier" class="fad fa-pen-alt text-sky hover:text-sky-700 cursor-pointer"></i>
                                <i @click="destroyWarehouse(warehouse)" title="Supprimer" class="fad fa-trash text-red-500 hover:text-red-600 cursor-pointer"></i>
                            </div>
                        </td>
                    </tr>
                </template>
            </Datatables>
        </div>

        <JetDialogModal :show="manageModal" @close="closeModal" max-width="2xl">
            <template #title> {{ warehouse ? 'Modifier l\' entrepôt' : 'Ajouter un nouvel entrepôt' }}</template>

            <template #content>
                <div class="grid grid-cols-2 gap-4">
                    <div class="col-span-1 sm:col-span-1">
                        <JetLabelRequired for="departure_country_id" value="Pays" />
                        <Multiselect
                            @change="onCountrySelected()"
                            :searchable="true"
                            :options="countries"
                            v-model="form.country_id"
                            placeholder="Sélectionner le pays de l'entrepôt"
                        />
                        <JetInputError :message="form.errors.country_id" />
                    </div>
                    <div class="col-span-1 sm:col-span-1">
                        <JetLabelRequired for="label" value="Libellé" />
                        <JetInput id="label" type="text" v-model="form.label" maxlength="80"
                            placeholder="Entrer le libellé" autocomplete="off"
                        />
                        <JetInputError :message="form.errors.label" />
                    </div>
                    <transition
                        leave-to-class="transform opacity-0 scale-95"
                        enter-from-class="transform opacity-0 scale-95"
                        enter-to-class="transform opacity-100 scale-100"
                        leave-from-class="transform opacity-100 scale-100"
                        leave-active-class="transition ease-in duration-75"
                        enter-active-class="transition ease-out duration-400"
                    >
                        <div v-if="countrySelected"  class="col-span-2">
                            <div class="flex w-full mb-3 text-yellow-600 bg-gray-input border border-yellow-500 rounded-md px-5 py-2 text-md lg:text-sm">
                                <span class="mr-2">Ajuster la position de l'entrepôt en utilisant le maqueur</span>
                                (<img title="Cliquer sur le marqueur pour arrêter sa danse."
                                    class="cursor-pointer hover:opacity-60"
                                    @click.prevent="ajustementMarker"
                                    width="20" height="20" src="/assets/img/maker.svg"
                                />)
                            </div>
                            <GoogleMapGeoInput
                                class="mt-3"
                                height="300px"
                                :draggable="true"
                                :id="addressFormMapId"
                                :lat-value="form.latitude"
                                :lng-value="form.longitude"
                                lat-input-id="add_latitude"
                                lng-input-id="add_longitude"
                                @map-value-changed="changeAddressGeoPosition"
                            />
                        </div>
                    </transition>
                    <div class="col-span-2">
                        <JetLabelRequired for="description" value="Description" />
                        <textarea class="form-input w-full" v-model="form.description"
                            id="description" cols="30" rows="3" maxlength="135"
                            placeholder="Entrer une brève description de la localisation de l'entrépôt ..."
                        ></textarea>
                        <JetInputError :message="form.errors.description" />
                    </div>
                </div>
            </template>

            <template #footer>
                <BotiButton @click="closeModal" text="Quitter" color="red">
                    <template #icon><i class="fad fa-times-circle text-xs ml-1"></i></template>
                </BotiButton>

                <BotiButton v-if="warehouse" :loader="form.processing" :disabled="form.processing"
                    color="sky"
                    @click="updateWarehouse"
                    class="ml-2" :class="{ 'opacity-25': form.processing }"
                    text="modifier"
                >
                    <svg v-if="form.processing" class="animate-spin w-4 h-4 fill-current shrink-0" viewBox="0 0 16 16">
                        <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z"></path>
                    </svg>
                    <template #icon>
                        <i class="fad fa-user-edit text-xs ml-1"></i>
                    </template>
                </BotiButton>

                <BotiButton v-else :loader="form.processing" :disabled="form.processing"
                    @click="storeWarehouse"
                    class="ml-2" :class="{ 'opacity-25': form.processing }"
                    text="Enregistrer"
                >
                    <svg v-if="form.processing" class="animate-spin w-4 h-4 fill-current shrink-0" viewBox="0 0 16 16">
                        <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z"></path>
                    </svg>
                    <template #icon>
                        <i class="fad fa-check-circle text-xs ml-1"></i>
                    </template>
                </BotiButton>
            </template>
        </JetDialogModal>
    </AppLayout>
</template>

<script>
    import JetLabel from '@/Jetstream/Label'
    import JetInput from '@/Jetstream/Input'
    import BotiButton from '@/Components/Button'
    import Multiselect from '@vueform/multiselect'
    import AppLayout from '@/Layouts/AppLayout.vue'
    import { Head } from '@inertiajs/inertia-vue3';
    import Datatables from '@/Components/Datatables'
    import JetInputError from '@/Jetstream/InputError'
    import JetDialogModal from '@/Jetstream/DialogModal'
    import JetLabelRequired from '@/Jetstream/LabelRequired'
    import GoogleMapGeoInput from '@/Components/GoogleMapGeoInput.vue'

    export default {
        components: {
            Head,
            JetLabel,
            JetInput,
            AppLayout,
            BotiButton,
            Datatables,
            Multiselect,
            JetInputError,
            JetDialogModal,
            JetLabelRequired,
            GoogleMapGeoInput
        },

        props: {
            countries: Object,
            warehouses: Object
        },

        data() {
            return {
                manageModal: false,
                countrySelected: null,
                warehouse: null,
                addressFormMapId: 'addressFormMapId',
                form: this.$inertia.form({})
            }
        },

        methods: {
            onCountrySelected() {
                setTimeout(() => {
                   this.ajustementMarker()
                }, 1000);
            },

            getCountry(id) {
                return this.countries.find(country => {
                    return country.value === id
                });
            },

            ajustementMarker() {
                this.countrySelected = this.getCountry(this.form.country_id)

                this.form.latitude = this.countrySelected.latitude
                this.form.longitude = this.countrySelected.longitude

                this.emitter.emit('addMarker', { lat: this.form.latitude, lng: this.form.longitude, title: this.countrySelected.label })
            },

            /* Atraiter */
            changeAddressGeoPosition ({id, pos}) {
                if (id === this.addressFormMapId) {
                    this.form.latitude = pos.lat
                    this.form.longitude = pos.lng
                }
            },

            closeModal() {
                this.manageModal = false
                this.countrySelected = null
                this.form.clearErrors()
                this.form.reset()
            },

            showModal(warehouse) {
                if (warehouse) {
                    this.warehouse = warehouse
                    this.form = this.$inertia.form(warehouse)
                    this.countrySelected = this.getCountry(this.form.country_id)
                } else {
                    this.warehouse = null
                    this.form = this.$inertia.form({
                        label: null,
                        description: null,
                        country_id: null,
                        latitude: null,
                        longitude: null
                    })
                }
                this.manageModal = true
            },

            storeWarehouse() {
                this.form.post(route('warehouses.store'), {
                    preserveScroll: true,
                    onSuccess: () => {
                        this.closeModal()
                        this.$swal.fire(
                            'Entrepôt',
                            'Entrepôt ajouté avec succès.',
                            'success'
                        )
                    },
                })
            },

            updateWarehouse() {
                this.form.put(route('warehouses.update', this.warehouse), {
                    preserveScroll: true,
                    onSuccess: () => {
                        this.closeModal()
                        this.$swal.fire(
                            'Entrepôt',
                            'Entrepôt modifié avec succès.',
                            'success'
                        )
                    },
                })
            },

            destroyWarehouse(warehouse) {
                this.$swal.queue([{
                    title: "Entrepôt",
                    text: "Etes vous sûr de vouloir supprimer l'entrpôt ?",
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: 'rgb(241, 162, 0)',
                    cancelButtonColor: '#bd081c',
                    confirmButtonText: "Supprimer",
                    showLoaderOnConfirm: true,
                    preConfirm: () => {
                        return this.$inertia.delete(route('warehouses.destroy', {'warehouse': warehouse}), {
                            preserveScroll: true,
                            preserveState: true,
                            onSuccess: () => {
                                this.$swal.fire(
                                    'Entrepôt',
                                    'Entrepôt supprimé avec succès.',
                                    'success'
                                )
                            },
                            onError: (error) => {
                                this.$swal.fire({
                                    icon: 'error',
                                    title: error.message
                                })
                            }
                        })
                    }
                }])
            }
        }
    }
</script>